import { useEffect, useRef, useState, type ReactNode } from "react";
import { GuestMediaDeferred } from "./publicIdentityMedia";
import { Icon } from "@/shared/ui";

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * 底部抽屉（模态）：“全部居民”（原来整页的居民卡片）与“暂时不知道在哪”的名单都放在这里。
 * - 关着时整块带 hidden（不显示、读屏读不到），内容照常挂着：再打开时头像已经退到哪一级、滚到哪都还在；
 *   没打开过时里面的访客头像先不取图（GuestMediaDeferred），免得访客首屏和日常卡抢图（UI-FIRST-IMPRESSION-116）；
 * - 打开时焦点进抽屉，Tab 只在抽屉里转；Esc、右上角关上、点抽屉外的暗处都能关；关了焦点回到打开它的那个按钮（returnFocusTo）；
 * - 打开时背景不滚：public-map.css 的 :root:has(.ps-pmap-drawer:not([hidden])) 收起整页滚动，抽屉自己的内容区单独滚。
 * 不用共享的 Sheet：那是给地图用的非模态面板（不挡背景、不收滚动）。
 */
export function ResidentsDrawer({
  open,
  onClose,
  returnFocusTo,
  titleId,
  title,
  closeLabel = "收起全部居民",
  testId = "residents-drawer",
  children,
}: {
  open: boolean;
  onClose: () => void;
  returnFocusTo: HTMLElement | null;
  titleId: string;
  title: string;
  closeLabel?: string;
  testId?: string;
  children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const [opened, setOpened] = useState(open);
  if (open && !opened) setOpened(true);
  const closeRef = useRef(onClose);
  const returnRef = useRef(returnFocusTo);
  useEffect(() => {
    closeRef.current = onClose;
    returnRef.current = returnFocusTo;
  });
  useEffect(() => {
    if (!open) return;
    const root = panel.current;
    root?.focus({ preventScroll: true });
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeRef.current();
        return;
      }
      if (event.key !== "Tab" || !root) return;
      const items = [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => !el.closest("[hidden]"));
      if (!items.length) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!root.contains(active)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && (active === first || active === root)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      const back = returnRef.current;
      if (back?.isConnected) back.focus({ preventScroll: true });
    };
  }, [open]);
  return (
    <div className="ps-pmap-drawer" hidden={!open} data-testid={testId}>
      <div className="ps-pmap-drawer__backdrop" aria-hidden="true" onClick={onClose} />
      <div ref={panel} className="ps-pmap-drawer__panel" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} data-motion={open ? "enter" : undefined}>
        <div className="ps-pmap-drawer__head">
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="ps-pmap-drawer__close" aria-label={closeLabel} onClick={onClose}>
            <Icon name="close" size={20} />
          </button>
        </div>
        <div className="ps-pmap-drawer__body"><GuestMediaDeferred.Provider value={!opened}>{children}</GuestMediaDeferred.Provider></div>
      </div>
    </div>
  );
}
